import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { COMMENTS } from '../../shared/constants/api-routes.constants';
import type { Comment } from '../../shared/components/comments/models/comments-service.interface';
import { HttpClientService } from './http-client.service';

/** Response shape from GET api/comments/thread (CommentThreadGetResponseDto). */
interface CommentThreadGetResponseDto {
  readonly thread: {
    readonly guid: string;
    readonly contextType: string;
    readonly target: { readonly caseId?: number; readonly incidentId?: number; readonly type?: string };
    readonly createdAt: string;
  };
  readonly comments: readonly {
    readonly guid: string;
    readonly content: string;
    readonly contentPlainText?: string;
    readonly createdAt: string;
    readonly isDeleted: boolean;
    readonly author: { readonly guid: string; readonly displayName: string };
    readonly mentions: readonly { readonly guid: string; readonly displayName: string }[];
  }[];
}

/** Request body for POST api/comments (CommentThreadDto). */
export interface CreateCommentRequestBody {
  readonly contextType: string;
  readonly target: { caseId: number } | { incidentId: number };
  readonly authorUserId: string;
  readonly content: string;
  readonly contentPlainText: string;
  readonly parentCommentId: number | null;
}

const COMMENT_AUTHOR_AVATAR_PLACEHOLDER = 'https://i.pravatar.cc/40?img=11';

/** Strips HTML to plain text; mention spans become their text content (e.g. @Gabriel). */
export function htmlToPlainText(html: string): string {
  if (!html?.trim()) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body?.textContent ?? '').trim();
  } catch {
    return html.replace(/<[^>]*>/g, '').trim();
  }
}

function mapThreadResponseToComments(res: CommentThreadGetResponseDto): Comment[] {
  return res.comments
    .filter((c) => !c.isDeleted)
    .map((c) => ({
      id: c.guid,
      author: {
        id: c.author.guid,
        name: c.author.displayName ?? '',
        avatarUrl: COMMENT_AUTHOR_AVATAR_PLACEHOLDER,
      },
      content: c.content ?? '',
      createdAt: new Date(c.createdAt),
      taggedUsers: (c.mentions ?? []).map((m) => m.guid),
    }));
}

@Injectable({ providedIn: 'root' })
export class CommentsService {
  private readonly http = inject(HttpClientService);

  /**
   * Fetches the comment thread for a case or an incident.
   * Pass exactly one of caseId or incidentId.
   */
  getThread(params: { caseId?: number; incidentId?: number }): Observable<readonly Comment[]> {
    const { caseId, incidentId } = params;
    let httpParams = new HttpParams();
    if (caseId != null) {
      httpParams = httpParams.set('caseId', String(caseId));
    } else if (incidentId != null) {
      httpParams = httpParams.set('incidentId', String(incidentId));
    }
    return this.http.get<CommentThreadGetResponseDto>(COMMENTS.THREAD, { params: httpParams }).pipe(
      map(mapThreadResponseToComments),
      catchError(() => of([]))
    );
  }

  /**
   * Creates a comment (or a new thread with one comment) via POST api/comments.
   */
  addComment(body: CreateCommentRequestBody): Observable<void> {
    return this.http.post<unknown>(COMMENTS.BASE, body).pipe(map(() => undefined));
  }
}
